import { formatUrl } from 'url-lib';
import Cookies from 'cookies-js';

import { fetchJSON } from 'js-utils/http';
import { pruneObject } from '@eb/destination-utils';
import omit from 'lodash/omit';
import moment from 'js-utils/moment-timezone';

import { deepKeysToSnake, keysCamelToSnake } from 'js-utils/transformation';
import { CURRENT_FUTURE, QUALITY } from '../constants';

const searchUrl = '/api/v3/destination/search/';
const searchAutocompleteSuggestionsUrl =
    '/api/v3/destination/search/autocomplete/';

const shouldShowDebugInfo = (isStaff, history) => {
    if (!history) {
        return false;
    }

    return isStaff && history.location.hash === '#debug';
};

const shouldOverrideJanus = (isStaff, experimentOverride) =>
    isStaff && experimentOverride.experimentName !== 'none';

// Check if the client_timezone of the query is correct. If we
// received it from the server, it may not be correct because
// it was guessed with GeoIP rather than browser-side JS.
// There may not be any client_timezone at all, this is currently only
// sent for online searches.
const CLIENT_TIMEZONE_COOKIE_NAME = 'client_timezone';
const repairClientTimezone = (searchQuery) => {
    if (searchQuery.clientTimezone) {
        const momentGuessedTimezone = moment.tz.guess();

        if (
            momentGuessedTimezone &&
            searchQuery.clientTimezone !== momentGuessedTimezone
        ) {
            Cookies.expire(CLIENT_TIMEZONE_COOKIE_NAME);
            Cookies.set(CLIENT_TIMEZONE_COOKIE_NAME, momentGuessedTimezone);

            return {
                ...searchQuery,
                clientTimezone: momentGuessedTimezone,
            };
        }
    }

    return searchQuery;
};

export const search = ({
    searchQuery,
    experimentOverride,
    isStaff,
    history,
}) => {
    // The search API allows either a bbox or a WOF placeID but not both
    let searchQueryFiltered =
        searchQuery.bbox && searchQuery.places
            ? omit(searchQuery, 'places')
            : searchQuery;

    searchQueryFiltered = repairClientTimezone(searchQueryFiltered);

    let requestParameters = {
        event_search: deepKeysToSnake(
            pruneObject({
                ...searchQueryFiltered,
                page_size: 20,
            }),
        ),
        'expand.destination_event': [
            'primary_venue',
            'image',
            'ticket_availability',
            'saves',
            'series',
            'my_collections',
            'event_sales_status',
        ],
    };

    if (shouldShowDebugInfo(isStaff, history)) {
        requestParameters = {
            ...requestParameters,
            debug: true,
        };
    }

    if (shouldOverrideJanus(isStaff, experimentOverride)) {
        requestParameters = {
            ...requestParameters,
            debug_experiment_overrides: {
                [experimentOverride.experimentName]: experimentOverride.variant,
            },
        };
    }

    const fetchReq = fetchJSON(searchUrl, {
        method: 'POST',
        body: JSON.stringify(requestParameters),
    });

    return fetchReq;
};

export const searchAutocompleteSuggestions = ({
    placeId,
    onlineEventsOnly,
    query,
}) => {
    let queryParams = {
        placeId,
        q: query,
        completionTypes: ['event', 'query'],
        'expand.destination_event': ['primary_venue', 'image'],
    };

    if (onlineEventsOnly) {
        queryParams.onlineEventsOnly = true;
    }

    queryParams = keysCamelToSnake(queryParams);
    return fetchJSON(formatUrl(searchAutocompleteSuggestionsUrl, queryParams));
};

export const searchSuggestionsForCity = ({ placeId, onlineEventsOnly }) =>
    fetchJSON(searchUrl, {
        method: 'POST',
        body: JSON.stringify({
            event_search: {
                places: placeId ? [placeId] : [],
                online_events_only: onlineEventsOnly,
                dates: CURRENT_FUTURE,
                sort: QUALITY,
                aggs: {
                    tags: {},
                },
            },
            'expand.destination_event': [
                'primary_venue',
                'image',
                'ticket_availability',
                'saves',
                'my_collections',
            ],
        }),
    });
